import { CartAPI } from '../../api/cart';
import { getProductById } from '../../api/preorder';
import { formatPrice } from '../../dynamicBundle/index.js';
import {
  buildUrlWithLocalization,
  getSessionStorage,
  getThemeSettings,
  isProductPage,
  setSessionStorage,
  setWindowLocation,
  splitGoogleFontStyle,
} from '../../utils';
import {
  VOLUME_BUNDLE_ACTION,
  VOLUME_MENU_ITEM_SELECTOR,
  KEY_VOLUME_BUNDLE,
  KEY_VOLUME_BUNDLE_DATA_SESSION,
  RECOMMENDATION_SELECTOR,
  VOLUME_BUTTON_ADD_BUNDLE_TO_CART_SELECTOR,
  VOLUME_BUNDLE_DEFAULT_SETTINGS,
} from '../constants';
import { closeVolumeBundleModal } from '../events/modal-events';

/**
 * @type {import('../index.d').volumeBundleData}
 */
export const volumeBundleData = async (
  context,
  bundle,
  { optionData, action, target, position }
) => {
  const [getVolumeBundleData, setVolumeBundleData] = context.useContextState(
    `${KEY_VOLUME_BUNDLE}-${bundle.widgetId}`,
    {}
  );

  const widgetId = bundle.widgetId || '';
  const widgetDesignSettings =
    context?.app?.recommendationWidgets?.['dynamic-bundle']?.[widgetId]?.widgetDesignSettings || {};

  let product = bundle.products[0];

  if (isProductPage(context) && position === 'volumeBlock') {
    const fetchedProduct = await getProductById(context, window.boostSDData.product.id);
    const matchedProduct = bundle.products.find((p) => p.id === fetchedProduct.id);
    product = matchedProduct || fetchedProduct;
  }

  if (!product) return;

  product.variants = product.variants?.filter(filterProductAvailable) || [];

  let selectedVariant = product.variants.reduce((minVariant, currentVariant) => {
    return parseFloat(currentVariant.price) < parseFloat(minVariant.price)
      ? currentVariant
      : minVariant;
  });

  if (action === VOLUME_BUNDLE_ACTION.changeVariant) {
    selectedVariant = product.variants.find(
      (variant) => variant.id === Number(optionData?.optionData?.variantId)
    );
  }

  const { price: originPrice = 0, title = '', id = '', image = '' } = selectedVariant;

  product.selectedOption = {
    rawPrice: Number(originPrice),
    title,
    price: formatPrice(context, originPrice),
    originPrice: formatPrice(context, originPrice),
    minPrice: formatPrice(context, product.price_min),
    id,
    image: image ?? product.images_info?.[0]?.src,
  };

  const discounts = Array.isArray(bundle.discounts) ? bundle.discounts : [];
  const currencyRate = Number(window.Shopify?.currency?.rate || 1);
  bundle.bundleOptions = discounts.length
    ? discounts.map((discount, index) => {
        const discountedPrice =
          discount.discount_type === 'percentage'
            ? originPrice * discount.item -
              (originPrice * discount.item * discount.discount_value) / 100
            : Math.max(0, originPrice * discount.item - discount.discount_value * currencyRate);

        return {
          id: `bundle-option-${index + 1}`,
          checked: widgetDesignSettings.bundleHighlightOption === index + 1,
          items: discount.item,
          discountLabel: `Buy ${discount.item} items and save ${
            discount.discount_type === 'percentage'
              ? `${discount.discount_value}%`
              : formatPrice(context, discount.discount_value * currencyRate)
          }`,
          rawOriginalPrice: originPrice * discount.item,
          rawDiscountedPrice: discountedPrice,
          originalPrice: formatPrice(context, originPrice * discount.item),
          discountedPrice: formatPrice(context, discountedPrice),
          discountValueDisplay:
            discount.discount_type === 'percentage'
              ? `${discount.discount_value}%`
              : formatPrice(context, discount.discount_value * currencyRate),
          selectedDropdownOptions: {
            variantId: selectedVariant.id,
            discountedPrice: formatPrice(context, discountedPrice),
            originalPrice: formatPrice(context, originPrice * discount.item),
            rawDiscountedPrice: discountedPrice,
            title: selectedVariant.title,
            image: selectedVariant.image || product.images_info?.[0]?.src,
          },
        };
      })
    : [
        {
          id: 'bundle-option-0',
          checked: widgetDesignSettings.bundleHighlightOption === 0,
          items: 1,
          discountLabel: 'No Discount',
          rawOriginalPrice: originPrice,
          rawDiscountedPrice: originPrice,
          originalPrice: formatPrice(context, originPrice),
          discountedPrice: formatPrice(context, originPrice),
          discountValueDisplay: '0%',
          selectedDropdownOptions: {
            variantId: selectedVariant.id,
            discountedPrice: formatPrice(context, originPrice),
            originalPrice: formatPrice(context, originPrice),
            rawDiscountedPrice: originPrice,
            title: selectedVariant.title,
            image: selectedVariant.image || product.images_info?.[0]?.src,
          },
        },
      ];

  if (target instanceof NodeList) {
    target = target[0];
  }

  const selectedItems =
    Number(target?.closest(VOLUME_MENU_ITEM_SELECTOR)?.dataset?.items) ||
    bundle.bundleOptions[0].items;
  bundle.selectedVariant = bundle.bundleOptions.find((option) => option.items === selectedItems);
  bundle.matchedProduct = product;
  bundle.productMinPrice = formatPrice(context, product.price_min);

  const selectedBundleOption = bundle.bundleOptions.find((option) => option.checked);
  bundle.selectedOption = {
    ...selectedBundleOption,
    discountedPrice: formatPrice(context, selectedBundleOption.rawDiscountedPrice),
    originalPrice: formatPrice(context, selectedBundleOption.rawOriginalPrice),
  };

  setVolumeBundleData(bundle);

  const totalBundlePrice = bundle.selectedOption?.discountedPrice || 0;
  const newBundleDataSession = {
    ...getSessionStorage(KEY_VOLUME_BUNDLE_DATA_SESSION),
    [bundle.widgetId]: {
      widgetId: bundle.widgetId,
      bundle_type: bundle.bundle_type,
      recommendationType: bundle.recommendation_type,
      primary_algorithm: bundle.primary_algorithm || '',
      secondary_algorithm: bundle.secondary_algorithm || '',
      totalBundlePrice,
      bundleProductId: product.id,
    },
  };
  setSessionStorage(KEY_VOLUME_BUNDLE_DATA_SESSION, newBundleDataSession);

  return bundle;
};

/**
 * @type {import('../index.d').addVolumeBundleToCart}
 */
export const addVolumeBundleToCart = async (context, target) => {
  const widgetId = target?.closest(RECOMMENDATION_SELECTOR)?.dataset?.widgetId;
  const volumeBundleData = getVolumeBundleDataByWidgetId(context, widgetId);

  if (
    !volumeBundleData ||
    !Array.isArray(volumeBundleData.products) ||
    volumeBundleData.products.length === 0
  ) {
    console.error('No valid product found in the bundle.');
    return;
  }

  const data = [];

  const selectedOption = volumeBundleData.selectedOption;

  data.push({
    id: selectedOption.selectedDropdownOptions.variantId,
    quantity: selectedOption.items,
    properties: {
      BoostBundle: '',
      _boostBundleId: volumeBundleData.widgetId,
      _boostBundleType: volumeBundleData.bundle_type,
      _boostItemPrice: selectedOption.selectedDropdownOptions.rawDiscountedPrice,
      _boostItemTitle: selectedOption.selectedDropdownOptions.title,
      _boostBundleProducts: volumeBundleData.matchedProduct.id,
    },
  });

  const btnAddToCart = target?.closest(VOLUME_BUTTON_ADD_BUNDLE_TO_CART_SELECTOR);
  const ctaBtnText = btnAddToCart?.querySelector('span');
  const textAddBundleAddToCart = ctaBtnText?.innerText || 'Add bundle to cart';

  const element = ctaBtnText || target;

  element.textContent =
    context.app?.translation?.productItem?.atcAddingToCartBtnLabel || 'Adding...';
  btnAddToCart.setAttribute('disabled', true);

  try {
    const res = await CartAPI.addMultiProductToCart(data);
    if (res.status === 200) {
      element.textContent =
        context.app?.translation?.productItem?.atcAddedToCartBtnLabel || 'Added!';

      const { enableCart, autoOpenAjaxCartOnAddToCart = true } =
        getThemeSettings(context)?.cart || {};
      const { noneCartOption = false } = window.boostWidgetIntegration || {};
      const shouldOpenCart = !noneCartOption && autoOpenAjaxCartOnAddToCart;

      /**
       * Communicate through event because ATC action handler belong to ProductGridFilter or RCM context
       * State from Cart context can't access other contexts and trigger re-render
       */
      if (enableCart) {
        const event = shouldOpenCart ? 'boost-sd-open-cart' : 'boost-sd-re-render-cart';
        window.dispatchEvent(new CustomEvent(event));
        closeVolumeBundleModal(context);
      } else {
        setWindowLocation(buildUrlWithLocalization('cart'));
      }
    } else {
      element.textContent =
        context.app?.translation?.productItem?.atcFailedToCartBtnLabel || 'Failed!';

      btnAddToCart.removeAttribute('disabled');
    }
  } catch {
    element.textContent =
      context.app?.translation?.productItem?.atcFailedToCartBtnLabel || 'Failed!';
  }

  setTimeout(function updateTextAddBundleToCart() {
    element.textContent = textAddBundleAddToCart;
    btnAddToCart.removeAttribute('disabled');
  }, 500);
};

/**
 * @type {import('../index.d').getVolumeBundleDataByWidgetId}
 */
export const getVolumeBundleDataByWidgetId = (context, widgetId) => {
  const [getVolumeBundleData] = context.useContextState(`${KEY_VOLUME_BUNDLE}-${widgetId}`, {});

  return {
    ...getVolumeBundleData(),
  };
};

/**
 * @type {import('../index.d').updateVolumeBundleData}
 */
export const updateVolumeBundleData = async (context, widgetId, data, action, target, position) => {
  const [getVolumeBundleData, setVolumeBundleData] = context.useContextState(
    `${KEY_VOLUME_BUNDLE}-${widgetId}`,
    {}
  );
  const oldDataVolumeBundle = getVolumeBundleData();

  if (!oldDataVolumeBundle) {
    console.error('Bundle data not found');
    return;
  }

  const newBundleData = await volumeBundleData(context, oldDataVolumeBundle, {
    optionData: data,
    action,
    target,
    position,
  });

  const checkedBundleOption = newBundleData.bundleOptions.find((option) => option.checked);

  const mergedBundleData = {
    ...oldDataVolumeBundle,
    matchedProduct: newBundleData.matchedProduct,
    selectedVariant: newBundleData.selectedVariant,
    selectedOption: checkedBundleOption
      ? {
          ...checkedBundleOption,
          discountedPrice: formatPrice(context, checkedBundleOption.rawDiscountedPrice),
          originalPrice: formatPrice(context, checkedBundleOption.rawOriginalPrice),
        }
      : null,
  };

  setVolumeBundleData(mergedBundleData);
  return mergedBundleData;
};

/**
 * @type {import('../index.d').filterProductAvailable}
 */
const filterProductAvailable = (product) => {
  return product?.available;
};

/**
 * @type {import('../index.d').getVolumeBundleSettingsByWidgetId}
 */
export const getVolumeBundleSettingsByWidgetId = (context, widgetId) => {
  let widgetDesignSettings =
    context?.app?.recommendationWidgets?.['dynamic-bundle']?.[widgetId]?.widgetDesignSettings || {};

  const dynamicDefaultSettings =
    context?.app?.recommendationWidgets?.dynamicBundleDefaultSettings ||
    VOLUME_BUNDLE_DEFAULT_SETTINGS;

  widgetDesignSettings = {
    ...dynamicDefaultSettings,
    ...widgetDesignSettings,
  };

  const { titleFontStyle } = widgetDesignSettings;
  const { fontWeight, fontStyle } = splitGoogleFontStyle(titleFontStyle);

  return {
    widgetDesignSettings: {
      ...widgetDesignSettings,
      titleFontStyle: fontStyle,
      titleFontWeight: fontWeight,
    },
  };
};
